import { useCallback, useEffect, useRef, useState } from "react";

import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import invariant from "tiny-invariant";
import { createPortal } from "react-dom";
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

import { getPageName, getPageSubtitle } from "./hooks";
import { AddPageIndicator, DragPreview, DropIndicator } from "./components";

import { TSectionPage, TemplatePageType, getTemplatePageData, isTemplatePageData } from "models";
import { Icon } from "utils/ui";
import { cn } from "utils/helpers";

type _GrabbableState =
  | {
      type: "idle";
    }
  | {
      type: "preview";
      container: HTMLElement;
    }
  | {
      type: "is-dragging";
    }
  | {
      type: "is-dragging-over";
      closestEdge: Edge | null;
    };

const idle: _GrabbableState = { type: "idle" };

export const GrabbableButton = ({
  pageElement,
  selected,
  showAddIndicator,
  onPageSelect,
  onAddPage,
  onPageDuplicate,
  onPageDelete,
}: _GrabbableButtonProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<_GrabbableState>(idle);

  useEffect(() => {
    const element = ref.current;

    // invariant - https://github.com/alexreardon/pdnd-react-tailwind/blob/main/src/task.tsx
    invariant(element, "element should not be null");

    return combine(
      draggable({
        element,
        getInitialData() {
          return getTemplatePageData(pageElement);
        },
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({
              x: "16px",
              y: "8px",
            }),
            render({ container }) {
              setState({ type: "preview", container });
            },
          });
        },
        onDragStart() {
          setState({ type: "is-dragging" });
        },
        onDrop() {
          setState(idle);
        },
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          // not allowing dropping on yourself
          if (source.element === element) {
            return false;
          }

          if (source.data.taskId === "task-0") {
            return false;
          }

          // only allowing tasks to be dropped on me
          return isTemplatePageData(source.data);
        },
        getData({ input }) {
          const data = getTemplatePageData(pageElement);
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ["top", "bottom"],
          });
        },
        getIsSticky() {
          return true;
        },
        onDragEnter({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState({ type: "is-dragging-over", closestEdge });
        },
        onDrag({ self }) {
          const closestEdge = extractClosestEdge(self.data);

          // Only need to update react state if nothing has changed.
          // Prevents re-rendering.
          setState((current) => {
            if (current.type === "is-dragging-over" && current.closestEdge === closestEdge) {
              return current;
            }
            return { type: "is-dragging-over", closestEdge };
          });
        },
        onDragLeave() {
          setState(idle);
        },
        onDrop() {
          setState(idle);
        },
      })
    );
  }, [pageElement]);

  /**
   * Select the page when not dragging
   */
  const onSelect = useCallback((): void => {
    if (state.type === "idle") {
      onPageSelect();
    }
  }, [state.type, onPageSelect]);

  const isCustomPage = pageElement.type === TemplatePageType.customPage;
  const subtitle = getPageSubtitle(pageElement.type);
  const pageName = getPageName(pageElement.type);

  const stateClasses = selected
    ? "border-[var(--primary-400)] shadow-[inset_3px_0_0_var(--primary-400)] bg-[var(--primary-50)]"
    : isCustomPage
      ? "border-[var(--surface-border)] bg-[var(--surface-a)]"
      : "border-[var(--surface-border)] bg-[var(--surface-b)]";

  return (
    <>
      <div>
        <div
          // Adding data-attribute as a way to query for this for our post drop flash
          data-task-id={pageElement.id}
          ref={ref}
          className={cn([
            "group select-none flex flex-row items-center gap-2 w-full min-h-12 mb-2 py-2.5 px-3 rounded-[10px] border cursor-pointer hover:border-[var(--primary-200)]",
            stateClasses,
          ])}
          onClick={onSelect}
        >
          {/** LEADING AFFORDANCE — grip for draggable custom sections, dot for fixed */}
          {isCustomPage ? (
            <span className="flex-none flex text-[var(--surface-400)] cursor-grab" title="Drag to reorder">
              <Icon name="GripVertical" size={14} strokeWidth={2} />
            </span>
          ) : (
            <span className="flex-none w-[13px] flex justify-center">
              <span className="w-2 h-2 rounded-full bg-[var(--surface-400)]" />
            </span>
          )}

          {/** LABEL + SUBTITLE */}
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm font-bold text-neutral-700">{pageName}</div>
            {subtitle ? <div className="text-xs mt-px text-[var(--text-color-secondary)]">{subtitle}</div> : null}
          </div>

          {/** TRAILING AFFORDANCE — duplicate/delete on custom sections */}
          {isCustomPage ? (
            <div className="flex-none flex items-center gap-0.5">
              <button
                type="button"
                title="Duplicate custom section"
                className="flex-none flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-80 text-[var(--text-color-secondary)] bg-transparent border-0 rounded p-0.5 hover:opacity-100 hover:bg-[var(--surface-c)]"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onPageDuplicate();
                }}
              >
                <Icon name="Copy" size={14} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                title="Delete custom section"
                className="flex-none flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-80 text-[var(--text-color-secondary)] bg-transparent border-0 rounded p-0.5 hover:opacity-100 hover:bg-[var(--surface-c)]"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onPageDelete(ev);
                }}
              >
                <Icon name="X" size={14} strokeWidth={2.2} />
              </button>
            </div>
          ) : null}
        </div>

        {/** INSERTION ZONE — drop a new section into this gap (never above the cover) */}
        {state.type === "is-dragging-over" && state.closestEdge ? (
          <DropIndicator />
        ) : showAddIndicator ? (
          <AddPageIndicator onClick={onAddPage} />
        ) : null}
      </div>
      {state.type === "preview" ? createPortal(<DragPreview pageElement={pageElement} />, state.container) : null}
    </>
  );
};

interface _GrabbableButtonProps {
  selected: boolean;
  pageElement: TSectionPage;
  /** Only show the "+ Add section" row below this page in specific slots (before Contents, before Defect List, at the very end). */
  showAddIndicator: boolean;
  onPageSelect: () => void;
  onAddPage: () => void;
  onPageDuplicate: () => void;
  onPageDelete: (event: any) => void;
}
